import React from 'react';
import { Box, Grid, Typography } from '@material-ui/core';
import { ResponsivePie } from '@nivo/pie';
import Color from 'color';
import { IGame, GameModes } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import { TeamColors } from 'utilities/team_colors';
import { getScoreAdvantage, getControlPointColors, AdvantagePoint } from './KillAdvantageGraph';

interface Props {
  game: IGame;
}

interface HillScore {
  id: string;
  label: string;
  value: number;
  color: string;
  textColor: string;
}

/**
 * How much of each team's score came from each hill. Between two score snapshots, the points a team
 * gained are credited to the hill that was active at the time (the same rule the score graph uses to
 * color its line).
 */
const getScoreByHill = (points: AdvantagePoint[], team: 1 | 2): Map<string, number> => {
  let totals = new Map<string, number>();
  let previous = 0;
  points.forEach((p) => {
    let score = team == 1 ? p.team1 : p.team2;
    let gained = score - previous;
    previous = score;
    if(gained <= 0)
      return;
    let hill = p.controlPoint || 'Unknown';
    totals.set(hill, (totals.get(hill) || 0) + gained);
  });
  return totals;
};

const TeamHillPie: React.FC<{team: 1 | 2, points: AdvantagePoint[], colors: Map<string, string>}> = ({team, points, colors}) => {
  let totals = getScoreByHill(points, team);
  let teamTotal = Array.from(totals.values()).reduce((sum, value) => sum + value, 0);
  // every hill in the game is listed, including ones the team got nothing from
  let hills = Array.from(new Set([...Array.from(colors.keys()), ...Array.from(totals.keys())]));
  let data: HillScore[] = hills.map((hill) => {
    let color = colors.get(hill) || '#9e9e9e';
    return {
      id: hill,
      label: hill,
      value: totals.get(hill) || 0,
      color,
      textColor: Color(color).isLight() ? '#212121' : '#ffffff',
    };
  });
  let teamColor = TeamColors[team - 1];

  return (
    <Box>
      <Typography variant="subtitle1" style={{color: teamColor, fontWeight: 600}}>
        Team {team} score by hill {teamTotal > 0 ? `(${teamTotal.toLocaleString()})` : ''}
      </Typography>
      {teamTotal == 0 ?
        <Typography style={{opacity: 0.7, padding: '16px 0'}}>Team {team} hasn't scored yet.</Typography>
      :
        <div style={{height: 300}}>
          <ResponsivePie
            data={data.filter((slice) => slice.value > 0)}
            colors={(datum: any) => datum.data.color}
            theme={{fontSize: 14}}
            margin={{top: 20, right: 120, bottom: 20, left: 120}}
            sortByValue={true}
            innerRadius={0.5}
            cornerRadius={5}
            activeOuterRadiusOffset={10}
            borderWidth={1}
            borderColor={{from: 'color', modifiers: [['darker', 0.2]]}}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={"#ffffff"}
            arcLinkLabelsStraightLength={10}
            arcLinkLabelsColor={{from: 'color'}}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor={(d: any) => d.data.textColor}
            arcLabel={(d: any) => `${Math.round((d.value / teamTotal) * 100)}%`}
            tooltip={({datum}: any) => (
              <div style={{background: 'rgba(20,20,20,0.95)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '6px 10px', color: '#e0e0e0', fontSize: '0.85em', whiteSpace: 'nowrap'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                  <span style={{display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: datum.color}} />
                  <b>{datum.label}</b>
                </div>
                <div>Team {team}: {datum.value.toLocaleString()} points</div>
                <div style={{color: '#bdbdbd'}}>{Math.round((datum.value / teamTotal) * 100)}% of the team's score</div>
              </div>
            )}
            legends={[]}
          />
        </div>
      }
      <Box mt={1}>
        {data.map((slice) => (
          <Box key={slice.id} display="grid" gridTemplateColumns="14px minmax(0, 1fr) auto auto" alignItems="center" style={{gap: 8, fontSize: '0.85em', padding: '2px 0', opacity: slice.value > 0 ? 1 : 0.6}}>
            <span style={{display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: slice.color}} />
            <span>{slice.label}</span>
            <span>{slice.value.toLocaleString()} pts</span>
            <span style={{color: '#9e9e9e', minWidth: 36, textAlign: 'right'}}>{teamTotal > 0 ? Math.round((slice.value / teamTotal) * 100) : 0}%</span>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** King of the hill only: each team's score broken down by hill, to show where they scored and struggled. */
export const KothControlPointPies: React.FC<Props> = ({game}) => {
  if(game.mode != GameModes.KingoftheHill)
    return null;
  let points = getScoreAdvantage(game);
  let colors = getControlPointColors(points);
  if(colors.size == 0)
    return null;
  return (
    <Box p={1}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TeamHillPie team={1} points={points} colors={colors} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TeamHillPie team={2} points={points} colors={colors} />
        </Grid>
      </Grid>
    </Box>
  );
}
